import { getLogger } from '@/lib/logger/logger';

const logger = getLogger('IframeStateMachine');

export type IframeState =
  'idle' | 'loading' | 'loaded' | 'initializing' | 'ready' | 'error' | 'disposed';

/** 허용된 상태 전이 테이블 */
const TRANSITIONS: Record<IframeState, readonly IframeState[]> = {
  idle: ['loading', 'disposed'],
  loading: ['loaded', 'error', 'disposed'],
  loaded: ['initializing', 'error', 'disposed'],
  initializing: ['ready', 'error', 'disposed'],
  ready: ['error', 'disposed'],
  error: ['idle', 'loading', 'disposed'],
  disposed: [],
} as const;

export type IframeStateListener = (
  newState: IframeState,
  prevState: IframeState,
  error?: Error,
) => void;

/**
 * SDK iframe 상태 머신.
 * idle → loading → loaded → initializing → ready → error → disposed
 */
export class IframeStateMachine {
  private _state: IframeState = 'idle';
  private readonly listeners = new Set<IframeStateListener>();
  private _lastError: Error | null = null;
  private readonly name: string;

  constructor(name: string) {
    this.name = name;
  }

  get state(): IframeState {
    return this._state;
  }

  get lastError(): Error | null {
    return this._lastError;
  }

  /** 상태 전이 시도. 유효하지 않은 전이면 false 반환. */
  transition(to: IframeState, error?: Error): boolean {
    const from = this._state;
    const allowed = TRANSITIONS[from];

    if (!allowed.includes(to)) {
      logger.warn(`[${this.name}] Invalid transition: ${from} → ${to}`);
      return false;
    }

    this._state = to;
    if (error) this._lastError = error;
    if (to === 'idle') this._lastError = null;

    logger.info(`[${this.name}] State: ${from} → ${to}`);

    for (const listener of this.listeners) {
      try {
        listener(to, from, error);
      } catch (e) {
        logger.error(`[${this.name}] Listener error:`, e instanceof Error ? e.message : String(e));
      }
    }

    return true;
  }

  /** 리스너 등록. 해제 함수 반환. */
  onStateChange(listener: IframeStateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** 특정 상태인지 확인 */
  is(state: IframeState): boolean {
    return this._state === state;
  }

  /** 리소스 해제 */
  dispose(): void {
    this.transition('disposed');
    this.listeners.clear();
  }

  /** idle로 리셋 (error 상태에서 재시도 시) */
  reset(): boolean {
    if (this._state === 'error') {
      return this.transition('idle');
    }
    logger.warn(`[${this.name}] Cannot reset from state: ${this._state}`);
    return false;
  }
}
